/**
 * sourcing/render-price.ts — 가격 응답 popover 렌더 (rfq 버블 클릭)
 *
 * SourcingView.ts 에서 분리 (M4, 2026-07-08 — deal-detail 분해 계획 v2 패턴 이식).
 * 이동만 — 동작·마크업 변화 없음. 전부 "state → HTML 문자열" 순수 함수.
 * popover 는 document.body 포탈(.sourcing-price-layer)에 그려지므로
 * 이벤트는 뷰의 수동 바인딩(_onPriceClick/_onPriceInput)이 처리.
 */

import { escapeHtml, catalogHitDescription } from '../../lib/cells';
import { RFQ_MEMO_TAGS, SOURCED_CONDITION_OPTIONS, type PriceState } from './types';

// 첨부 썸네일 1개(이미지=미리보기, PDF=문서 아이콘) + 삭제 버튼. (마이그141)
function renderAttachThumb(a: PriceState['attachments'][number]): string {
  const isImg = a.mediaType.startsWith('image/');
  const inner = isImg
    ? `<img src="${escapeHtml(a.url)}" alt="${escapeHtml(a.origName ?? '')}" loading="lazy" />`
    : `<span class="sourcing-attach-doc">📄</span>`;
  return `<div class="sourcing-price-attach" title="${escapeHtml(a.origName ?? '')}">
    <a href="${escapeHtml(a.url)}" target="_blank" rel="noopener">${inner}</a>
    <button type="button" class="sourcing-price-attach-del" data-action="price-image-del" data-key="${escapeHtml(a.key)}" title="삭제">×</button>
  </div>`;
}

// 버블 rect(viewport) → popover 위치. 내용 전체(약 540px)를 한 번에 보이게 높이 넉넉히.
export function pricePos(rect: { left: number; top: number; bottom: number }): { left: number; top: number; maxH: number } {
  const GAP = 6;
  const NEED = 540;   // 내용 전체 높이 추정
  const left = Math.max(12, Math.min(window.innerWidth - 356, rect.left));
  const belowSpace = window.innerHeight - rect.bottom - 12;
  const aboveSpace = rect.top - 12;
  // 아래에 내용이 다 들어가면 아래, 아니면 위가 더 넓으면 위로(내용만큼 끌어올림).
  const top = (belowSpace >= NEED || belowSpace >= aboveSpace)
    ? rect.bottom + GAP
    : Math.max(12, rect.top - NEED - GAP);
  // 내용이 다 보이게 넉넉히(단, 뷰포트는 안 넘게).
  const maxH = Math.min(NEED + 60, window.innerHeight - 24);
  return { left, top, maxH };
}

export function renderAltResults(p: PriceState): string {
  if (p.altSearching && p.altResults.length === 0) {
    return `<div class="sourcing-alt-note">검색 중…</div>`;
  }
  if (!p.altPart.trim() || p.altResults.length === 0) return '';
  return p.altResults.map((hit) => {
    const part = String(hit.option_part ?? '');
    const desc = catalogHitDescription(hit);
    return `
        <div class="sourcing-alt-result">
          <div class="sourcing-alt-result-info">
            <div class="sourcing-alt-result-part">${escapeHtml(part)}</div>
            ${desc ? `<div class="sourcing-alt-result-desc">${escapeHtml(desc)}</div>` : ''}
          </div>
          <button type="button" class="sourcing-alt-pick-btn" data-action="price-alt-pick" data-part="${escapeHtml(part)}">선택</button>
        </div>`;
  }).join('');
}

// ── 가격 응답 popover (기획 레이아웃: 라벨형 세로 필드) ──
export function renderPricePopover(p: PriceState): string {
  if (!p.open) return '';
  // 앵커(버블) 기준 위치(viewport) → 문서 좌표로 변환(스크롤 포함). absolute 라 스크롤 시 함께 이동.
  const vp = pricePos({ left: p.x, top: p.anchorTop, bottom: p.y });
  const fLeft = vp.left + window.scrollX;
  const fTop  = vp.top  + window.scrollY;
  const maxH  = vp.maxH;
  const curOpts  = ['USD', 'KRW'].map((c) => `<option value="${c}" ${c === p.currency ? 'selected' : ''}>${c}</option>`).join('');
  const condOpts = ['<option value="">대체 컨디션 선택</option>',
    ...SOURCED_CONDITION_OPTIONS.map((o) => `<option value="${escapeHtml(o)}" ${o === p.condition ? 'selected' : ''}>${escapeHtml(o)}</option>`)].join('');
  const tagSummary = p.tags.length ? escapeHtml(p.tags.join(', ')) : '선택 없음';
  const tagMenu = p.tagsOpen
    ? `<div class="sourcing-tag-menu">
         ${RFQ_MEMO_TAGS.map((t) => `<button type="button" class="sourcing-tag-option ${p.tags.includes(t) ? 'is-on' : ''}"
               data-action="price-tag" data-tag="${escapeHtml(t)}">${p.tags.includes(t) ? '☑' : '☐'} ${escapeHtml(t)}</button>`).join('')}
       </div>`
    : '';
  return `
      <div class="sourcing-price-overlay" data-action="price-close"></div>
      <div class="sourcing-price-popover" style="left:${fLeft}px;top:${fTop}px;max-height:${maxH}px;overflow-y:auto">
        <div class="sourcing-price-head">
          <strong class="sourcing-price-title">${escapeHtml(p.supplierName)}</strong>
          <button class="deal-rfq-modal-close" type="button" data-action="price-close" aria-label="닫기">✕</button>
        </div>
        ${p.error ? `<div class="alert alert-error">${escapeHtml(p.error)}</div>` : ''}

        <div class="sourcing-price-controls">
          <input class="input sourcing-price-amount" type="number" min="0" step="0.01"
                 data-action="price-field" data-field="amount"
                 value="${escapeHtml(p.amount)}" placeholder="${p.currency === 'USD' ? '$' : '₩'}" ${p.noStock ? 'disabled' : ''}>
          <select class="input sourcing-price-cur" data-action="price-field" data-field="currency" ${p.noStock ? 'disabled' : ''}>${curOpts}</select>
          <button type="button" class="sourcing-price-stock ${p.noStock ? 'on' : ''}" data-action="price-stock">${p.noStock ? '없음' : '재고 없음'}</button>
        </div>

        <div class="sourcing-price-field">
          <label>회신 수량 <span class="sourcing-price-req">요청 ${escapeHtml(String(p.requestedQty))}EA</span></label>
          <div class="sourcing-price-qtyrow">
            <input class="input sourcing-price-qty" type="number" min="0" step="1"
                   data-action="price-field" data-field="respondedQty"
                   value="${escapeHtml(p.respondedQty)}" placeholder="EA">
            <span class="sourcing-price-qty-unit">EA</span>
          </div>
          <p class="sourcing-price-hint">요청 수량보다 적게 회신된 경우 입력하면 RFQ 뱃지에 표시됩니다.</p>
        </div>

        <div class="sourcing-price-field">
          <label>대체 컨디션</label>
          <select class="input" data-action="price-field" data-field="condition">${condOpts}</select>
        </div>

        <div class="sourcing-price-field">
          <label>대체품목</label>
          <div class="sourcing-alt-wrap">
            <input class="input" type="text" data-action="price-alt-input" data-field="altPart" autocomplete="off"
                   value="${escapeHtml(p.altPart)}" placeholder="파트넘버 검색">
            <div class="sourcing-alt-results" id="price-alt-results">${renderAltResults(p)}</div>
          </div>
        </div>

        <div class="sourcing-price-field">
          <span class="sourcing-price-field-title">RFQ 메모</span>
          <div class="sourcing-tag-dd">
            <button type="button" class="sourcing-tag-dd-summary" data-action="price-tags-toggle">
              <span>${tagSummary}</span><span class="sourcing-tag-dd-caret">▾</span>
            </button>
            ${tagMenu}
          </div>
        </div>

        <div class="sourcing-price-field">
          <label>기타 메모</label>
          <textarea class="input sourcing-price-memo" data-action="price-field" data-field="memo"
                    placeholder="리드타임, 유효기간 등">${escapeHtml(p.memo)}</textarea>
        </div>

        <div class="sourcing-price-field">
          <label>거래처 수신 이미지/PDF</label>
          <div class="sourcing-price-attachments">
            ${p.attachments.map((a) => renderAttachThumb(a)).join('')}
            <label class="sourcing-price-attach-add${p.uploading ? ' is-uploading' : ''}">
              <span class="sourcing-price-attach-plus">＋</span>
              <span>${p.uploading ? '업로드…' : '첨부'}</span>
              <input type="file" accept="image/*,application/pdf" multiple
                     data-action="price-image-pick" ${p.uploading ? 'disabled' : ''} />
            </label>
          </div>
        </div>

        <div class="sourcing-price-actions">
          ${(() => {
            // 가격 입력된 RFQ 만 구매요청 허용(재고없음·미입력 차단). 이미 요청됨이면 취소는 항상 가능.
            const canRequest = p.amount.trim() !== '' && !p.noStock;
            const disabled = !p.purchaseRequested && !canRequest;
            return `<button class="btn ${p.purchaseRequested ? 'btn-secondary' : 'btn-primary'} sourcing-price-purchase" type="button"
                  data-action="price-purchase-toggle" data-rfq-id="${escapeHtml(p.rfqId)}"
                  data-requested="${p.purchaseRequested ? '1' : '0'}" ${disabled ? 'disabled' : ''}
                  title="${disabled ? '가격 입력·저장 후 구매요청할 수 있습니다' : ''}">${p.purchaseRequested ? '🛒 구매요청 취소' : '🛒 구매요청'}</button>`;
          })()}
          <button class="btn btn-primary sourcing-price-save" type="button" data-action="price-save" ${p.saving ? 'disabled' : ''}>${p.saving ? '저장 중…' : '저장'}</button>
        </div>
      </div>`;
}
